'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useLayoutEffect, useState } from 'react';

import { useContextTabsContext } from '@/features/context-tabs';
import { ColorDot } from '@web/ui';

import styles from './SideNav.module.scss';
import { NavGroup, NavItem } from './types';
import { UserNavItem } from './UserNavItem';

import type { ReactNode } from 'react';

type SidePanelProps = {
  items: NavItem[];
  footerTopContent?: (ctx: { isExpanded: boolean }) => ReactNode;
};

const SIDENAV_EXPANDED_STORAGE_KEY = 'netnet:sidenav-expanded';
const SIDENAV_COLLAPSED_WIDTH = 'var(--nn-layout-sidebar-width-collapsed)';
const SIDENAV_EXPANDED_WIDTH = 'var(--nn-layout-sidebar-width-expanded)';
const SIDENAV_ACTIVE_WIDTH_CSS_VAR = '--nn-side-active-width';

function isGroup(item: NavItem): item is NavGroup {
  return (item as NavGroup).children !== undefined;
}

function normalizePath(path: string) {
  const normalized = path.replace(/\/+$/, '');
  return normalized || '/';
}

function isRouteActive(currentPath: string, href: string) {
  const current = normalizePath(currentPath);
  const target = normalizePath(href);
  return current === target || current.startsWith(`${target}/`);
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName;
  return (
    target.isContentEditable ||
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT'
  );
}

function NavIcon({
  item,
}: {
  item: Pick<
    NavItem,
    'iconIdle' | 'iconHover' | 'showIndicator' | 'indicatorLabel'
  >;
}) {
  return (
    <span className={styles.iconWrapDual} aria-hidden>
      <span className={styles.iconIdle}>{item.iconIdle}</span>
      <span className={styles.iconHover}>{item.iconHover}</span>
      {item.showIndicator ? (
        <span className={styles.itemIndicator} aria-label={item.indicatorLabel}>
          <ColorDot size="lg" color="var(--nn-color-primary)" />
        </span>
      ) : null}
    </span>
  );
}

export function SideNav({ items, footerTopContent }: SidePanelProps) {
  const pathname = usePathname();
  const router = useRouter();
  const contextTabs = useContextTabsContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const primaryItems = items.filter(
    (item) => (item.layoutGroup ?? 'primary') === 'primary',
  );

  const secondaryItems = items.filter(
    (item) => (item.layoutGroup ?? 'primary') === 'secondary',
  );

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(SIDENAV_EXPANDED_STORAGE_KEY);
      if (saved !== null) {
        // eslint-disable-next-line
        setIsExpanded(saved === 'true');
      }
    } catch {
      // * localStorage can be unavailable in private/restricted contexts.
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SIDENAV_EXPANDED_STORAGE_KEY,
        String(isExpanded),
      );
    } catch {
      // * Ignore persistence failures and keep runtime state functional.
    }
  }, [isExpanded]);

  useLayoutEffect(() => {
    // * Keep app shell offset in sync with actual sidenav width.
    document.documentElement.style.setProperty(
      SIDENAV_ACTIVE_WIDTH_CSS_VAR,
      isExpanded ? SIDENAV_EXPANDED_WIDTH : SIDENAV_COLLAPSED_WIDTH,
    );
  }, [isExpanded]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isToggleShortcut =
        (event.ctrlKey || event.metaKey) &&
        !event.altKey &&
        !event.shiftKey &&
        event.key.toLowerCase() === 'b';

      if (!isToggleShortcut || isEditableTarget(event.target)) {
        return;
      }

      event.preventDefault();
      setIsExpanded((current) => !current);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleGroupClick = (group: NavGroup) => {
    const { key, defaultHref } = group;

    if (defaultHref) {
      const hasActiveChild = group.children.some((sub) =>
        isRouteActive(pathname, sub.href),
      );

      if (!isExpanded) {
        setIsExpanded(true);
        setOpenGroups((state) => ({ ...state, [key]: true }));
        if (!isRouteActive(pathname, defaultHref)) {
          if (contextTabs) contextTabs.navigateInActiveTab(defaultHref);
          else router.push(defaultHref);
        }
        return;
      }

      const isCurrentlyOpen = openGroups[key] ?? hasActiveChild;
      const nextOpen = !isCurrentlyOpen;
      const shouldNavigate = nextOpen && !isRouteActive(pathname, defaultHref);

      setOpenGroups((state) => ({ ...state, [key]: nextOpen }));

      if (shouldNavigate) {
        if (contextTabs) contextTabs.navigateInActiveTab(defaultHref);
        else router.push(defaultHref);
      }

      return;
    }

    if (!isExpanded) {
      setIsExpanded(true);
      setOpenGroups((state) => ({ ...state, [key]: true }));
      return;
    }

    setOpenGroups((state) => ({ ...state, [key]: !state[key] }));
  };

  const renderNavItem = (it: NavItem) => {
    if (!isGroup(it)) {
      const active = isRouteActive(pathname, it.href);
      const itemClassName = `${styles.item} ${active ? styles.itemActive : ''} ${
        it.disabled ? styles.itemDisabled : ''
      }`;

      if (it.disabled) {
        return (
          <button
            key={it.key}
            type="button"
            className={itemClassName}
            onClick={() => {
              if (it.disabledHref) {
                router.push(it.disabledHref);
              }
            }}
            aria-disabled="true"
          >
            <NavIcon item={it} />
            <span className={styles.label}>{it.label}</span>
          </button>
        );
      }

      return (
        <Link
          key={it.key}
          href={it.href}
          className={itemClassName}
          aria-current={active ? 'page' : undefined}
          prefetch
          onClick={(event) => {
            if (!contextTabs) return;
            event.preventDefault();
            contextTabs.navigateInActiveTab(it.href);
          }}
        >
          <NavIcon item={it} />
          <span className={styles.label}>{it.label}</span>
        </Link>
      );
    }

    const hasActiveChild = it.children.some((sub) =>
      isRouteActive(pathname, sub.href),
    );

    const isOpen = openGroups[it.key] ?? hasActiveChild;
    return (
      <div key={it.key} style={{ width: '100%' }}>
        <button
          type="button"
          className={`${styles.item} ${hasActiveChild ? styles.itemActive : ''}`}
          onClick={() => handleGroupClick(it)}
          aria-expanded={isOpen}
        >
          <NavIcon item={it} />
          <span className={styles.label}>{it.label}</span>
        </button>

        <div className={styles.submenu} data-open={isOpen}>
          {it.children.map((sub) => {
            const active = isRouteActive(pathname, sub.href);
            return (
              <Link
                key={sub.key}
                href={sub.href}
                className={`${styles.submenuItem} ${active ? styles.submenuItemActive : ''}`}
                aria-current={active ? 'page' : undefined}
                prefetch
                onClick={(event) => {
                  if (!contextTabs) return;
                  event.preventDefault();
                  contextTabs.navigateInActiveTab(sub.href);
                }}
              >
                <span className={styles.label}>{sub.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <aside
      className={styles.sidenav}
      aria-label="Workspace Navigation"
      data-expanded={isExpanded}
    >
      <div className={styles.items} role="navigation">
        <div className={styles.itemsPrimary}>
          {primaryItems.map(renderNavItem)}
        </div>
        {secondaryItems.length ? (
          <div className={styles.itemsSecondary}>
            {secondaryItems.map(renderNavItem)}
          </div>
        ) : null}
      </div>

      <div className={styles.userSection}>
        {footerTopContent && (
          <div className={styles.userSectionTop}>
            {footerTopContent({ isExpanded })}
          </div>
        )}

        <UserNavItem
          showSideNavToggle
          isSideNavExpanded={isExpanded}
          onToggleSideNav={() => setIsExpanded((current) => !current)}
        />
      </div>
    </aside>
  );
}
